import { getToken } from "./session";

const INDEX_PATH = /^\/[a-z]{2}\/index\/?$/;

let fullData = null;
let fullPromise = null;
let applyPromise = null;
let servedFull = false;
let fullFailed = false;

// Set once the full bundle was fetched in this tab, so a reload of the
// index keeps using it instead of the lite one.
const FULL_SEEN_KEY = "localizationFullSeen";

function markFullSeen() {
  try {
    window.sessionStorage.setItem(FULL_SEEN_KEY, "1");
  } catch (error) {}
}

function wasFullSeen() {
  try {
    return window.sessionStorage.getItem(FULL_SEEN_KEY) === "1";
  } catch (error) {
    return false;
  }
}

export function isIndexPath(pathname) {
  return INDEX_PATH.test(pathname || "");
}

export function isFullLoaded() {
  return fullData !== null;
}

export function didFullBundleFail() {
  return fullFailed;
}

function missingLocalizationError() {
  const error = new Error("localization file is missing");
  error.status = 404;
  return error;
}

function languageOnly(lng) {
  return String(lng || "en").split("-")[0];
}

function lngFromUrl(url) {
  const match = String(url).match(
    /\/(?:localization|translations)\/([a-zA-Z]+?)(?:\.index)?\.json/,
  );
  return match ? match[1] : "en";
}

function parseJson(text) {
  try {
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
}

function deliverFull(callback, data) {
  servedFull = true;
  callback(null, { status: 200, data: data });
}

function shouldUseLiteBundle() {
  if (fullData || fullPromise) return false;
  if (getToken()) return false;
  if (wasFullSeen()) return false;
  return isIndexPath(window.location.pathname);
}

function fetchFull(lng) {
  if (fullData) return Promise.resolve(fullData);
  if (fullFailed) return Promise.reject(missingLocalizationError());
  if (!fullPromise) {
    const language = languageOnly(lng);
    fullPromise = fetch(`/localization/${language}.json`)
      .then((response) => {
        if (!response.ok) throw missingLocalizationError();
        return response.text();
      })
      .then((text) => {
        const data = parseJson(text);
        if (!data || typeof data !== "object" || Array.isArray(data)) {
          throw missingLocalizationError();
        }
        fullData = data;
        markFullSeen();
        return fullData;
      })
      .catch((error) => {
        fullPromise = null;
        fullFailed = true;
        if (!error.status) error.status = 404;
        throw error;
      });
  }
  return fullPromise;
}

export function localizationLoadPath() {
  if (shouldUseLiteBundle()) return "/translations/{{lng}}.index.json";
  return "/localization/{{lng}}.json";
}

export function localizationRequest(options, url, payload, callback) {
  const lite = String(url).indexOf(".index.json") !== -1;
  const lng = lngFromUrl(url);

  if (!lite) {
    fetchFull(lng)
      .then((data) => deliverFull(callback, data))
      .catch((error) => callback(error, { status: 404 }));
    return;
  }

  fetch(url)
    .then((response) => {
      if (!response.ok || fullPromise || fullData) {
        return fetchFull(lng).then((data) => deliverFull(callback, data));
      }
      return response.text().then((text) => {
        const parsed = parseJson(text);
        if (fullPromise || fullData || !parsed || typeof parsed !== "object") {
          return fetchFull(lng).then((data) => deliverFull(callback, data));
        }
        callback(null, { status: 200, data: parsed });
      });
    })
    .catch(() => {
      fetchFull(lng)
        .then((data) => deliverFull(callback, data))
        .catch((error) => callback(error, { status: 404 }));
    });
}

export function loadFullBundle(i18n) {
  if (fullFailed) return Promise.resolve();
  if (applyPromise) return applyPromise;
  const lng = languageOnly(
    (i18n && (i18n.resolvedLanguage || i18n.language)) || "en",
  );
  const ns =
    (i18n && i18n.options && i18n.options.defaultNS) || "translation";
  applyPromise = fetchFull(lng)
    .then((data) => {
      if (!i18n || servedFull) return;
      i18n.addResourceBundle(lng, ns, data, true, true);
    })
    .catch(() => {
      fullFailed = true;
    });
  return applyPromise;
}

export const localizationBackend = {
  loadPath: localizationLoadPath,
  request: localizationRequest,
  crossDomain: true,
};
